/**
 * assets/colour-swatches.js
 *
 * Cross-product colour swatch navigation for the PDP. Clicking a swatch
 * fetches the target product's main-product section via the Section
 * Rendering API and swaps it in place of the current one, then re-runs
 * theme.Product() (the same reinit call Vantage's own quickview uses)
 * so the gallery, variant form and sticky bar all work on the new markup.
 * Falls back to a normal navigation if fetch fails or JS is unavailable.
 */
(function () {
  var SWAP_ROOT_SELECTOR = '[data-product-swap-root]';
  var BUNDLE_SAVE_ROOT_SELECTOR = '[data-bundle-save-swap-root]';
  // Generic marker for any other section whose content depends on the
  // current product (metafield-driven images/text, dynamic-source
  // bindings, etc). "Why you'll love it" (pdp-features) and "Specs &
  // materials" (pdp-specs) both read straight off `product`/block
  // settings resolved at render time, so — like the bundle-save
  // showcase — they sit outside the main swap root and go stale on a
  // colour swap unless they're fetched and swapped too.
  var EXTRA_SWAP_ROOT_SELECTOR = '[data-pdp-swap-section]';

  function getSwapRoot() {
    return document.querySelector(SWAP_ROOT_SELECTOR);
  }

  function initAllColoursModals(root) {
    var scope = root || document;
    if (!window.WAU || !window.WAU.Modal) return;

    scope.querySelectorAll('[data-family-id]').forEach(function (el) {
      var familyId = el.getAttribute('data-family-id');
      if (!familyId) return;

      var modalName = 'all-colours-' + familyId;

      if (document.querySelector('[data-wau-modal-content="' + modalName + '"]')) {
        // Re-run on every call (initial load and after each swatch swap): the
        // opener buttons are fresh DOM nodes after a swap with no listeners
        // of their own, even when the modal name is unchanged. WAU.Modal.init
        // re-queries and re-binds the current openers each time; it reuses
        // the existing modal element rather than creating a duplicate.
        window.WAU.Modal.init(modalName);
      }
    });
  }

  function syncAllColoursModalActiveState(currentPath) {
    // WAU.Modal creates the modal element once and reuses it forever (it
    // lives outside the swap root, in the persistent .js-modal-toggle-wrapper),
    // cloning the drawer's innerHTML at that first-creation moment. It never
    // refreshes that clone afterward, so its is-active markers stay frozen
    // on whichever colour was current at that first load. Update them here
    // instead of relying on the (stale) markup.
    document.querySelectorAll('[data-wau-modal^="all-colours-"] [data-colour-swatch]').forEach(function (el) {
      var swatchUrl = el.getAttribute('data-swatch-url');
      var isActive = false;
      if (swatchUrl) {
        try {
          isActive = new URL(swatchUrl, window.location.origin).pathname === currentPath;
        } catch (e) {
          isActive = swatchUrl === currentPath;
        }
      }
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  function closeAllColoursDrawer() {
    var openDrawer = document.querySelector('[data-wau-modal-target].colour-group__all-link');
    if (openDrawer && window.WAU && window.WAU.Modal) {
      var name = openDrawer.getAttribute('data-wau-modal-target');
      try {
        window.WAU.Modal._closeByName(name);
      } catch (e) {
        /* modal may not be open, ignore */
      }
    }
  }

  function swapInProduct(url, pushHistory) {
    var currentRoot = getSwapRoot();
    if (!currentRoot) {
      window.location.href = url;
      return;
    }

    var sectionId = currentRoot.getAttribute('data-section-id');
    if (!sectionId) {
      window.location.href = url;
      return;
    }

    // The standalone Bundle & Save showcase lives in its own section,
    // outside the main product section's swap root, so a swatch swap
    // wouldn't otherwise touch it: its per-tier images and (critically)
    // its own Add to Cart form's hidden variant id would stay frozen on
    // whichever colour loaded the page, silently adding the wrong item
    // to the cart. Fetch it alongside main via Shopify's multi-section
    // endpoint and swap both together.
    var bundleSaveRoot = document.querySelector(BUNDLE_SAVE_ROOT_SELECTOR);
    var bundleSaveSectionId = bundleSaveRoot && bundleSaveRoot.getAttribute('data-section-id');

    var extraRoots = Array.prototype.slice
      .call(document.querySelectorAll(EXTRA_SWAP_ROOT_SELECTOR))
      .map(function (root) {
        return { root: root, sectionId: root.getAttribute('data-section-id') };
      })
      .filter(function (entry) {
        return !!entry.sectionId;
      });

    var sectionIds = [sectionId];
    if (bundleSaveSectionId) sectionIds.push(bundleSaveSectionId);
    extraRoots.forEach(function (entry) {
      if (sectionIds.indexOf(entry.sectionId) === -1) sectionIds.push(entry.sectionId);
    });

    currentRoot.classList.add('is-swapping');
    if (bundleSaveRoot) bundleSaveRoot.classList.add('is-swapping');

    var separator = url.indexOf('?') === -1 ? '?' : '&';
    var fetchUrl = url + separator + 'sections=' + sectionIds.join(',');

    fetch(fetchUrl)
      .then(function (response) {
        if (!response.ok) throw new Error('Section fetch failed');
        return response.json();
      })
      .then(function (data) {
        var parser = new DOMParser();
        var mainDoc = parser.parseFromString(data[sectionId], 'text/html');
        var newRoot = mainDoc.querySelector(SWAP_ROOT_SELECTOR);

        if (!newRoot) throw new Error('Swap root not found in response');

        currentRoot.replaceWith(newRoot);
        newRoot.classList.remove('is-swapping');

        if (bundleSaveSectionId && bundleSaveRoot && data[bundleSaveSectionId]) {
          var bundleDoc = parser.parseFromString(data[bundleSaveSectionId], 'text/html');
          var newBundleRoot = bundleDoc.querySelector(BUNDLE_SAVE_ROOT_SELECTOR);
          if (newBundleRoot) {
            bundleSaveRoot.replaceWith(newBundleRoot);
          }
        }

        extraRoots.forEach(function (entry) {
          var html = data[entry.sectionId];
          if (!html) return;
          var extraDoc = parser.parseFromString(html, 'text/html');
          var newExtraRoot = extraDoc.querySelector(
            '[data-pdp-swap-section][data-section-id="' + entry.sectionId + '"]'
          );
          if (newExtraRoot) {
            entry.root.replaceWith(newExtraRoot);
          }
        });

        if (pushHistory) {
          window.history.pushState({ colourSwatchUrl: url }, '', url);
        }

        closeAllColoursDrawer();

        if (window.theme && typeof window.theme.Product === 'function') {
          window.theme.Product(newRoot);
        }
        if (window.Shopify && window.Shopify.theme && window.Shopify.theme.ajaxCart) {
          window.Shopify.theme.ajaxCart.init();
        }
        initAllColoursModals(newRoot);
        syncAllColoursModalActiveState(new URL(url, window.location.origin).pathname);

        document.dispatchEvent(
          new CustomEvent('colourswatch:swapped', { detail: { root: newRoot, url: url } })
        );
      })
      .catch(function () {
        window.location.href = url;
      });
  }

  document.addEventListener('click', function (event) {
    var swatch = event.target.closest('[data-colour-swatch]');
    if (!swatch) return;

    var url = swatch.getAttribute('data-swatch-url');
    if (!url) return;

    var currentPath = window.location.pathname;
    var swatchUrl = new URL(url, window.location.origin);
    if (swatchUrl.pathname === currentPath) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    swapInProduct(url, true);
  });

  window.addEventListener('popstate', function () {
    if (getSwapRoot()) {
      swapInProduct(window.location.href, false);
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    initAllColoursModals(document);
  });
})();
